import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarCheck,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Lock,
  MapPin,
  Minus,
  Plus,
  Send,
  Paperclip,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  cloturerSondageReunion,
  creerReunion,
  enregistrerCompteRendu,
  fetchReferentielReunions,
  joindreDocumentReunion,
  retirerDocumentReunion,
  fetchReunions,
  repondreReunion,
  supprimerReunion,
  type Reunion,
  type ReunionCreneau,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import Dialogue from '../components/Dialogue';

import { dateFr } from '../utils/dates';
const dateLongue = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const heure = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

function formatCreneau(c: { debut: string; fin: string }): string {
  const debut = new Date(c.debut);
  return `${dateLongue.format(debut)} — ${heure.format(debut)} à ${heure.format(new Date(c.fin))}`;
}

const couleurStatut: Record<string, string> = {
  sondage: 'bg-amber-50 text-amber-700',
  planifiee: 'bg-green-50 text-green-700',
  terminee: 'bg-blue-50 text-blue-700',
  annulee: 'bg-gray-100 text-gray-500 dark:text-gray-400 dark:bg-gray-700',
};

/** Les trois réponses possibles au sondage, dans l'ordre d'affichage. */
const reponses = [
  { valeur: 'oui', label: 'Disponible', icone: Check, actif: 'bg-green-600 text-white' },
  { valeur: 'peut_etre', label: 'Sous réserve', icone: Minus, actif: 'bg-amber-500 text-white' },
  { valeur: 'non', label: 'Indisponible', icone: X, actif: 'bg-red-600 text-white' },
] as const;

const creneauVide = { debut: '', fin: '' };

/**
 * Réunions de l'association : sondage de date, convocation, compte rendu.
 *
 * Le cycle est toujours le même — on propose des dates, chacun dit ses
 * disponibilités depuis le site ou depuis l'application installée, le bureau
 * arrête une date et tout le monde reçoit la convocation, puis le compte rendu.
 */
export default function BureauReunionsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [formOuvert, setFormOuvert] = useState(false);
  const [depliee, setDepliee] = useState<number | null>(null);

  const estConvoque =
    user?.roles?.includes('ROLE_VOLUNTEER') === true ||
    user?.roles?.includes('ROLE_BUREAU') === true ||
    user?.roles?.includes('ROLE_ADMIN') === true;

  const { data, isLoading } = useQuery({
    queryKey: ['reunions'],
    queryFn: fetchReunions,
    enabled: estConvoque,
  });

  const referentiel = useQuery({
    queryKey: ['reunions-referentiel'],
    queryFn: fetchReferentielReunions,
    enabled: estConvoque,
  });

  const rafraichir = (message: string) => {
    setNotice({ type: 'success', message });
    queryClient.invalidateQueries({ queryKey: ['reunions'] });
  };
  // Un refus du serveur s'affiche au centre. En bandeau, il paraissait
  // au-dessus d'un formulaire qu'on venait de faire défiler : on recliquait le
  // même bouton en croyant que rien ne s'était passé, et l'échec se répétait.
  const echouer = (e: Error) => setRefus(e.message);

  if (authLoading || (estConvoque && isLoading)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!estConvoque) {
    return (
      <div className="p-6 lg:p-10 text-center space-y-3 pb-24 lg:pb-0">
        <Lock className="w-12 h-12 mx-auto text-gray-300" />
        <h1 className="text-xl font-bold">Accès réservé</h1>
        <p className="text-gray-600 dark:text-gray-400">Les réunions sont réservées aux membres et bénévoles.</p>
      </div>
    );
  }

  const reunions = data?.reunions ?? [];
  const peutGerer = data?.peutGerer === true;
  const types: Record<string, string> = referentiel.data?.types ?? {};

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary-600" />
            Réunions
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Sondage de date, convocation et compte rendu.
          </p>
        </div>

        {peutGerer && (
          <button
            onClick={() => setFormOuvert(!formOuvert)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nouvelle réunion
          </button>
        )}
      </header>

      {notice && (
        <p
          className={`text-sm rounded-xl px-3 py-2 ${
            notice.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {notice.message}
        </p>
      )}

      {refus && (
        <Dialogue
          titre="Cette réunion n'a pas pu être enregistrée"
          message={refus}
          onFermer={() => setRefus(null)}
        />
      )}

      {peutGerer && formOuvert && (
        <FormulaireReunion
          types={types}
          onAnnuler={() => setFormOuvert(false)}
          onCree={(m) => {
            rafraichir(m);
            setFormOuvert(false);
          }}
          onEchec={echouer}
        />
      )}

      {reunions.length === 0 ? (
        <div className="card text-center py-10 space-y-2">
          <CalendarClock className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-500 dark:text-gray-400">
            Aucune réunion pour le moment.
            {peutGerer && ' Proposez des dates, chacun répondra depuis son espace.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {reunions.map((r) => (
            <li key={r.id}>
              <CarteReunion
                reunion={r}
                peutGerer={peutGerer}
                ouverte={depliee === r.id}
                onBasculer={() => setDepliee(depliee === r.id ? null : r.id)}
                onSucces={rafraichir}
                onEchec={echouer}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------ Création ---------------------------------- */

function FormulaireReunion({
  types,
  onAnnuler,
  onCree,
  onEchec,
}: {
  types: Record<string, string>;
  onAnnuler: () => void;
  onCree: (message: string) => void;
  onEchec: (e: Error) => void;
}) {
  const [titre, setTitre] = useState('');
  const [type, setType] = useState('membres');
  const [lieu, setLieu] = useState('');
  const [ordreDuJour, setOrdreDuJour] = useState('');
  const [sondageFermeLe, setSondageFermeLe] = useState('');
  const [creneaux, setCreneaux] = useState([{ ...creneauVide }]);
  const [notifier, setNotifier] = useState(true);

  const creation = useMutation({
    mutationFn: () =>
      creerReunion({
        titre: titre.trim(),
        type,
        lieu: lieu.trim() || null,
        ordreDuJour: ordreDuJour.trim() || null,
        sondageFermeLe: sondageFermeLe || null,
        creneaux: creneaux.filter((c) => c.debut && c.fin),
        notifier,
      }),
    onSuccess: (d) => onCree(d.message),
    onError: onEchec,
  });

  // Un créneau ne vaut que si sa fin suit son début. L'écran ne vérifiait que
  // la présence des deux dates : une fin antérieure partait au serveur, qui la
  // refusait — le bouton semblait sans effet, et l'échec n'apparaissait que
  // dans la console du navigateur.
  const valides = creneaux.filter((c) => c.debut && c.fin && c.fin > c.debut);
  const aLEnvers = creneaux.some((c) => c.debut && c.fin && c.fin <= c.debut);
  const invalide = titre.trim() === '' || valides.length === 0;

  return (
    <section className="card lg:p-6 space-y-4">
      <h2 className="font-semibold">Nouvelle réunion</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <label className="text-sm text-gray-600 lg:col-span-2 dark:text-gray-400">
          Titre
          <input
            className="input mt-1"
            placeholder="Préparation de la fête votive"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Qui est convoqué
          <select className="input mt-1" value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(types).map(([valeur, libelle]) => (
              <option key={valeur} value={valeur}>
                {libelle}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Lieu
          <input
            className="input mt-1"
            placeholder="Salle des fêtes"
            value={lieu}
            onChange={(e) => setLieu(e.target.value)}
          />
        </label>

        <label className="text-sm text-gray-600 lg:col-span-2 dark:text-gray-400">
          Ordre du jour
          <textarea
            className="input mt-1 min-h-[5rem]"
            placeholder="Les points à aborder, un par ligne."
            value={ordreDuJour}
            onChange={(e) => setOrdreDuJour(e.target.value)}
          />
        </label>

        <label className="text-sm text-gray-600 dark:text-gray-400">
          Réponses attendues avant le
          <input
            className="input mt-1"
            type="date"
            value={sondageFermeLe}
            onChange={(e) => setSondageFermeLe(e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Dates proposées</p>
        {creneaux.map((c, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className="input flex-1 min-w-[12rem]"
              type="datetime-local"
              value={c.debut}
              onChange={(e) => {
                const copie = [...creneaux];
                // La fin suit le début d'une heure tant qu'elle n'a pas été
                // réglée : proposer trois dates ne doit pas coûter six saisies.
                copie[i] = { ...copie[i], debut: e.target.value };
                if (!copie[i].fin && e.target.value) {
                  const fin = new Date(e.target.value);
                  fin.setHours(fin.getHours() + 1);
                  copie[i].fin = fin.toISOString().slice(0, 16);
                }
                setCreneaux(copie);
              }}
            />
            <span className="text-gray-400 text-sm">→</span>
            <input
              className="input flex-1 min-w-[12rem]"
              type="datetime-local"
              value={c.fin}
              onChange={(e) => {
                const copie = [...creneaux];
                copie[i] = { ...copie[i], fin: e.target.value };
                setCreneaux(copie);
              }}
            />
            {creneaux.length > 1 && (
              <button
                type="button"
                onClick={() => setCreneaux(creneaux.filter((_, j) => j !== i))}
                title="Retirer cette date"
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={() => setCreneaux([...creneaux, { ...creneauVide }])}
          className="text-sm text-primary-700 hover:text-primary-800 inline-flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> Proposer une autre date
        </button>
      </div>

      <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={notifier}
          onChange={(e) => setNotifier(e.target.checked)}
          className="mt-0.5"
        />
        Envoyer le sondage par e-mail aux personnes convoquées
      </label>

      {/* Dit avant de cliquer, et non après le refus du serveur. */}
      {aLEnvers && (
        <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
          Une date proposée se termine avant de commencer : corrigez-la, sinon elle ne comptera pas.
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => creation.mutate()}
          disabled={invalide || creation.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {creation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Créer et ouvrir le sondage
        </button>
        <button onClick={onAnnuler} className="text-sm text-gray-500 dark:text-gray-400">
          Annuler
        </button>
      </div>
    </section>
  );
}

/* -------------------------------- Carte ----------------------------------- */

function CarteReunion({
  reunion,
  peutGerer,
  ouverte,
  onBasculer,
  onSucces,
  onEchec,
}: {
  reunion: Reunion;
  peutGerer: boolean;
  ouverte: boolean;
  onBasculer: () => void;
  onSucces: (message: string) => void;
  onEchec: (e: Error) => void;
}) {
  const queryClient = useQueryClient();
  const [mesReponses, setMesReponses] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      reunion.creneaux.filter((c) => c.maReponse).map((c) => [c.id, c.maReponse as string]),
    ),
  );
  const [creneauChoisi, setCreneauChoisi] = useState<number | null>(null);
  const [lieu, setLieu] = useState(reunion.lieu ?? '');
  const [compteRendu, setCompteRendu] = useState(reunion.compteRendu ?? '');
  const champDocument = useRef<HTMLInputElement>(null);

  const invalider = () => queryClient.invalidateQueries({ queryKey: ['reunions'] });

  // Créneau le mieux placé : c'est celui que la clôture retiendra par défaut.
  const recommande = useMemo(
    () =>
      reunion.creneaux.reduce<ReunionCreneau | null>(
        (meilleur, c) => (meilleur === null || c.score > meilleur.score ? c : meilleur),
        null,
      ),
    [reunion.creneaux],
  );


  const repondre = useMutation({
    mutationFn: () => repondreReunion(reunion.id, mesReponses),
    onSuccess: (d) => {
      onSucces(d.message);
      invalider();
    },
    onError: onEchec,
  });

  const cloturer = useMutation({
    mutationFn: () =>
      cloturerSondageReunion(reunion.id, {
        creneauId: creneauChoisi ?? recommande?.id ?? null,
        lieu: lieu.trim() || null,
      }),
    onSuccess: (d) => {
      onSucces(d.message);
      invalider();
    },
    onError: onEchec,
  });

  const enregistrer = useMutation({
    mutationFn: (diffuser: boolean) =>
      enregistrerCompteRendu(reunion.id, {
        compteRendu: compteRendu.trim() || null,
        diffuser,
      }),
    onSuccess: (d) => {
      onSucces(d.message);
      invalider();
    },
    onError: onEchec,
  });

  const joindre = useMutation({
    mutationFn: (fichier: File) => joindreDocumentReunion(reunion.id, fichier),
    onSuccess: (d) => {
      onSucces(d.message);
      invalider();
    },
    onError: onEchec,
  });

  const detacher = useMutation({
    mutationFn: () => retirerDocumentReunion(reunion.id),
    onSuccess: (d) => {
      onSucces(d.message);
      invalider();
    },
    onError: onEchec,
  });

  const supprimer = useMutation({
    mutationFn: () => supprimerReunion(reunion.id),
    onSuccess: (d) => {
      onSucces(d.message);
      invalider();
    },
    onError: onEchec,
  });

  const aRepondu = reunion.creneaux.some((c) => c.maReponse);

  return (
    <article className="card space-y-3">
      <button
        type="button"
        onClick={onBasculer}
        className="w-full flex items-start justify-between gap-3 text-left"
      >
        <div className="min-w-0">
          <p className="font-semibold truncate">{reunion.titre}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`px-2 py-0.5 rounded-full ${
                couleurStatut[reunion.statut] ?? 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700'
              }`}
            >
              {reunion.statutLabel}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-800">
              {reunion.typeLabel}
            </span>
            {reunion.sondageOuvert && !aRepondu && (
              <span className="px-2 py-0.5 rounded-full bg-primary-600 text-white">
                À renseigner
              </span>
            )}
          </div>

          {reunion.dateRetenue && (
            <p className="mt-1.5 text-sm text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-0.5 dark:text-gray-400">
              <span className="inline-flex items-center gap-1">
                <CalendarCheck className="w-4 h-4 text-green-600" />
                {formatCreneau({ debut: reunion.dateRetenue, fin: reunion.finRetenue ?? reunion.dateRetenue })}
              </span>
              {reunion.lieu && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {reunion.lieu}
                </span>
              )}
            </p>
          )}
        </div>

        {ouverte ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>

      {ouverte && (
        <div className="space-y-4 pt-2 border-t border-gray-100 dark:border-gray-800">
          {reunion.ordreDuJour && (
            <div className="text-sm">
              <p className="font-medium text-gray-700 dark:text-gray-200">Ordre du jour</p>
              <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {reunion.ordreDuJour}
              </p>
            </div>
          )}

          {/* Sondage ouvert : chacun donne ses disponibilités. */}
          {reunion.sondageOuvert && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Vos disponibilités
              </p>

              {reunion.creneaux.map((c) => (
                <div
                  key={c.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-0"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-[12rem]">
                    {formatCreneau(c)}
                    {peutGerer && (
                      <span className="ml-2 text-xs text-gray-400">
                        {c.oui} oui · {c.peutEtre} réserve · {c.non} non
                      </span>
                    )}
                  </span>

                  <div className="inline-flex rounded-xl bg-gray-100 p-0.5">
                    {reponses.map((r) => {
                      const Icone = r.icone;
                      const actif = mesReponses[c.id] === r.valeur;

                      return (
                        <button
                          key={r.valeur}
                          type="button"
                          title={r.label}
                          onClick={() => setMesReponses({ ...mesReponses, [c.id]: r.valeur })}
                          className={`px-2.5 py-1 rounded-lg transition-colors ${
                            actif ? r.actif : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'
                          }`}
                        >
                          <Icone className="w-4 h-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button
                onClick={() => repondre.mutate()}
                disabled={repondre.isPending || Object.keys(mesReponses).length === 0}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {repondre.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Enregistrer mes disponibilités
              </button>
            </div>
          )}

          {/* Clôture : réservée au bureau. */}
          {peutGerer && reunion.sondageOuvert && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-medium text-amber-900">Arrêter la date</p>
              <p className="text-xs text-amber-800">
                La confirmation part immédiatement par e-mail à toutes les personnes convoquées,
                avec la date, l'heure et le lieu.
              </p>

              <select
                className="input"
                value={creneauChoisi ?? recommande?.id ?? ''}
                onChange={(e) => setCreneauChoisi(Number(e.target.value))}
              >
                {reunion.creneaux.map((c) => (
                  <option key={c.id} value={c.id}>
                    {formatCreneau(c)} — {c.oui} disponible(s)
                    {recommande?.id === c.id ? ' · le mieux placé' : ''}
                  </option>
                ))}
              </select>

              <input
                className="input"
                placeholder="Lieu de la réunion"
                value={lieu}
                onChange={(e) => setLieu(e.target.value)}
              />

              <button
                onClick={() => cloturer.mutate()}
                disabled={cloturer.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {cloturer.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Clôturer et convoquer
              </button>
            </div>
          )}

          {/* Détail nominatif des réponses, pour le bureau. */}
          {peutGerer && reunion.creneaux.some((c) => c.reponses.length > 0) && (
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900 dark:text-gray-400">
                Qui a répondu quoi
              </summary>
              <div className="mt-2 space-y-2">
                {reunion.creneaux.map((c) => (
                  <div key={c.id}>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{formatCreneau(c)}</p>
                    <ul className="text-xs text-gray-600 dark:text-gray-300">
                      {c.reponses.map((r, i) => (
                        <li key={i}>
                          {r.membre} —{' '}
                          {reponses.find((x) => x.valeur === r.disponibilite)?.label ??
                            r.disponibilite}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Compte rendu : rédigé par le bureau, lu par tous. */}
          {!reunion.sondageOuvert && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary-600" />
                Compte rendu
                {reunion.compteRenduPublieLe && (
                  <span className="text-xs font-normal text-gray-400">
                    diffusé le {dateFr(reunion.compteRenduPublieLe)}
                  </span>
                )}
              </p>

              {peutGerer ? (
                <>
                  <textarea
                    className="input min-h-[8rem]"
                    placeholder="Décisions prises, points reportés, prochaines échéances…"
                    value={compteRendu}
                    onChange={(e) => setCompteRendu(e.target.value)}
                  />

                  {/* Un compte rendu se rédige souvent ailleurs : le joindre
                      tel quel préserve la mise en forme, les tableaux et les
                      signatures qu'une recopie perdrait. */}
                  <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-3 space-y-2">
                    {reunion.compteRenduFichier ? (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />
                        <a
                          href={reunion.compteRenduFichier.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-primary-700 hover:underline truncate"
                        >
                          {reunion.compteRenduFichier.nom}
                        </a>
                        {reunion.compteRenduFichier.taille && (
                          <span className="text-xs text-gray-400">
                            {Math.round(reunion.compteRenduFichier.taille / 1024)} Ko
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => detacher.mutate()}
                          disabled={detacher.isPending}
                          title="Retirer le document"
                          className="ml-auto text-red-600 hover:text-red-700 disabled:opacity-40"
                        >
                          {detacher.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">Aucun document joint.</p>
                    )}

                    <button
                      type="button"
                      onClick={() => champDocument.current?.click()}
                      disabled={joindre.isPending}
                      className="btn-outline inline-flex items-center gap-2 text-sm py-1.5 px-3 disabled:opacity-50"
                    >
                      {joindre.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Paperclip className="w-4 h-4" />
                      )}
                      {reunion.compteRenduFichier ? 'Remplacer le document' : 'Joindre un document'}
                    </button>

                    <p className="text-xs text-gray-400">
                      PDF, DOC, DOCX, ODT, PNG, JPG ou WEBP, 15 Mo au plus. Il part en pièce
                      jointe avec le compte rendu.
                    </p>

                    <input
                      ref={champDocument}
                      type="file"
                      accept=".pdf,.doc,.docx,.odt,.png,.jpg,.jpeg,.webp"
                      className="hidden"
                      onChange={(e) => {
                        const fichier = e.target.files?.[0];
                        if (fichier) joindre.mutate(fichier);
                        // Remis à zéro pour que redéposer le même fichier
                        // déclenche bien l'envoi.
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => enregistrer.mutate(false)}
                      disabled={enregistrer.isPending}
                      className="btn-outline text-sm py-2 px-4 disabled:opacity-50"
                    >
                      Enregistrer
                    </button>
                    <button
                      onClick={() => enregistrer.mutate(true)}
                      disabled={
                        enregistrer.isPending ||
                        (compteRendu.trim() === '' && !reunion.compteRenduFichier)
                      }
                      className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4 disabled:opacity-50"
                    >
                      {enregistrer.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Enregistrer et diffuser
                    </button>
                  </div>
                </>
              ) : reunion.compteRendu || reunion.compteRenduFichier ? (
                <>
                  {reunion.compteRendu && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                      {reunion.compteRendu}
                    </p>
                  )}
                  {/* Le document est parfois tout le compte rendu : il doit se
                      télécharger sans passer par la boîte mail. */}
                  {reunion.compteRenduFichier && (
                    <a
                      href={reunion.compteRenduFichier.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:underline"
                    >
                      <Paperclip className="w-4 h-4" />
                      {reunion.compteRenduFichier.nom}
                    </a>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400">Pas encore rédigé.</p>
              )}
            </div>
          )}

          {peutGerer && (
            <button
              onClick={() => {
                if (window.confirm(`Supprimer la réunion « ${reunion.titre} » ?`)) {
                  supprimer.mutate();
                }
              }}
              disabled={supprimer.isPending}
              className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" /> Supprimer la réunion
            </button>
          )}
        </div>
      )}
    </article>
  );
}
